/**
 * FreeSuikaRefillDialog — web2: the game is free, so a pet that runs low on Suika is refilled
 * for free instead of at a till. Refills unlock BELOW `SUIKA_LOW_THRESHOLD` and fill up to
 * `SUIKA_FREE_REFILL_TARGET`; spending down is the cooldown (see freeSuikaRefillService).
 *
 * Shares the till's look (`SuikaPurchaseDialog.css`), which stays parked for when payments
 * return — never add a price, a wallet or a network picker here.
 */
import { useState, type ReactNode } from 'react';
import { Dialog } from '@radix-ui/themes';
import { GAME_CONSTANTS } from '@shared/domain/gameConstants';
import { refillSuikaFree } from '../../api/petBackendApi';
import { useToast } from '../ToastContainer';
import './SuikaPurchaseDialog.css';
import { useT } from '../../i18n/i18nContext';

interface FreeSuikaRefillDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Any Suikamon of the player. Suika is ONE wallet per player; the refill route takes a pet id
   * only to find whose wallet to top up.
   */
  petId: string;
  /** The player's wallet balance. */
  balance: number;
  hero?: ReactNode;
  /** Called after the refill lands so the screen re-reads the balance. */
  onRefilled: () => void;
}

export function FreeSuikaRefillDialog({ open, onClose, petId, balance, hero, onRefilled }: FreeSuikaRefillDialogProps) {
  const t = useT();
  const { showToast } = useToast();
  const [isPending, setIsPending] = useState(false);
  const canRefill = balance < GAME_CONSTANTS.SUIKA_LOW_THRESHOLD;

  const handleRefill = async () => {
    setIsPending(true);
    try {
      const result = await refillSuikaFree(petId);
      if (!result.success) {
        showToast(result.error || t('care.refill.failed'), 'error');
        return;
      }
      showToast(t('care.refill.success', { amount: result.amount ?? GAME_CONSTANTS.SUIKA_FREE_REFILL_TARGET }), 'success');
      onRefilled();
      onClose();
    } catch {
      showToast(t('care.refill.retry'), 'error');
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(next: boolean) => !next && onClose()}>
      <Dialog.Content className="suika-buy">
        <Dialog.Title as="h2" className="suika-buy__title">
          {t('care.refill.title')}
        </Dialog.Title>

        {hero && <div className="suika-buy__hero">{hero}</div>}

        <Dialog.Description size="2" className="suika-buy__lead">
          {canRefill
            ? t('care.refill.canRefill')
            : t('care.refill.locked', { threshold: GAME_CONSTANTS.SUIKA_LOW_THRESHOLD })}
        </Dialog.Description>

        <div className="suika-buy__tiers">
          <button
            type="button"
            className="suika-buy__tier suika-buy__tier--recommended"
            onClick={handleRefill}
            disabled={!canRefill || isPending}
          >
            <span className="suika-buy__tier-top">
              <span className="suika-buy__tier-price">{t('care.refill.free')}</span>
              <span className="suika-buy__tier-suika">{t('care.refill.amount', { amount: GAME_CONSTANTS.SUIKA_FREE_REFILL_TARGET })}</span>
            </span>
            <span className="suika-buy__tier-name">{isPending ? t('care.refill.refilling') : t('care.refill.refill')}</span>
          </button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
